import { useState, useEffect, useRef } from "react";
import { useAnim, useSEO } from "../hooks.jsx";
import { Link } from "react-router-dom";
import { PAGE_SEO, BREADCRUMBS, BREADCRUMB_ITEMS } from "../data.js";
import Breadcrumb from "../components/Breadcrumb.jsx";
import CtaBand from "../components/CtaBand.jsx";
import logoHeader from "../../assets/logo-header.png";

// ─────────────────────────────────────────────────────────────────────────────
// TUTORIAL INTERATTIVI — struttura dati
//
// Ogni tutorial: id, title, desc, icon, duration, steps[]
// Ogni step: title, desc, visual (JSX), screenshot (opzionale)
//
// FOTO REALI — per sostituire un'animazione con uno screenshot vero basta
// aggiungere `screenshot: require("../../assets/tutorial/nome-file.png")`
// alla proprietà dello step: viene mostrata al posto del mockup animato.
// Consigliata risoluzione ~1200×800px, senza dati sensibili di clienti reali.
// ─────────────────────────────────────────────────────────────────────────────

const BRAND = "#2563EB";
const BRAND2 = "#1D4ED8";

function StepVisual({ step }) {
  if (step.screenshot) {
    return (
      <div style={{ borderRadius: 18, overflow: "hidden", border: "1.5px solid var(--border)", boxShadow: "var(--sh)" }}>
        <img src={step.screenshot} alt={step.title} style={{ width: "100%", display: "block" }} />
      </div>
    );
  }
  return step.visual;
}

// ═══════════════════════════════════════════════════════════════════════════
// VISUAL — un unico riquadro pulito e centrato per ogni step, senza chrome
// finto di browser/sidebar: solo l'informazione essenziale, animata.
// ═══════════════════════════════════════════════════════════════════════════

function Frame({ children }) {
  return (
    <div style={{
      background: "#fff",
      border: "1.5px solid var(--border)",
      borderRadius: 20,
      padding: "2.75rem 2rem",
      minHeight: 280,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      gap: "1.1rem",
    }}>
      {children}
    </div>
  );
}

function Badge({ children, color = BRAND, bg = "rgba(37,99,235,.08)" }) {
  return (
    <span style={{
      fontSize: ".78rem", fontWeight: 700, padding: ".4rem .9rem", borderRadius: 100,
      background: bg, color, transition: "all .35s",
    }}>{children}</span>
  );
}

function BigIcon({ children, spin }) {
  return (
    <div style={{
      width: 68, height: 68, borderRadius: "50%",
      background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", boxShadow: "0 10px 24px rgba(37,99,235,.22)",
      transition: "transform .4s", transform: spin ? "scale(1.08)" : "scale(1)",
    }}>{children}</div>
  );
}

// ── Tutorial 1 — Configurazione iniziale ─────────────────────────────────────

function InfoAzienda() {
  const [i, setI] = useState(0);
  const rows = [["Nome azienda","Autonoleggio Rossi"],["Città","Milano"],["Telefono","+39 02 1234567"]];
  useEffect(() => { const t = setInterval(() => setI(v => (v+1)%3), 1500); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <BigIcon><IconBuilding size={28} /></BigIcon>
      <div>
        <div style={{ fontSize: ".78rem", color: "var(--muted)", fontWeight: 600, marginBottom: 4 }}>{rows[i][0]}</div>
        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--ink)" }}>{rows[i][1]}</div>
      </div>
      <Badge>Impostazioni → Azienda</Badge>
    </Frame>
  );
}

function LogoColori() {
  const [c, setC] = useState(0);
  const colors = ["#2563EB","#0F766E","#B45309","#7C3AED"];
  useEffect(() => { const t = setInterval(() => setC(v => (v+1)%4), 1200); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <div style={{ width: 68, height: 68, borderRadius: 20, background: colors[c], display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", transition: "background .5s", boxShadow: `0 10px 24px ${colors[c]}44` }}><IconPalette size={28} /></div>
      <div>
        <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--ink)", marginBottom: 8 }}>Il tuo colore, ovunque</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          {colors.map((cc, idx) => (
            <div key={cc} style={{ width: 18, height: 18, borderRadius: "50%", background: cc, border: idx === c ? "2.5px solid var(--ink)" : "2.5px solid transparent", transition: "border .3s" }} />
          ))}
        </div>
      </div>
      <Badge>Impostazioni → Sito pubblico</Badge>
    </Frame>
  );
}

function OrariApertura() {
  const [d, setD] = useState(0);
  const giorni = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
  useEffect(() => { const t = setInterval(() => setD(v => (v+1)%7), 500); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <BigIcon><IconClock size={28} /></BigIcon>
      <div style={{ display: "flex", gap: 6 }}>
        {giorni.map((g, i) => (
          <div key={g} style={{
            width: 30, height: 30, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: ".68rem", fontWeight: 800, transition: "all .3s",
            background: i === d ? BRAND : (i < 5 ? "rgba(27,45,107,.08)" : "#FEF2F2"),
            color: i === d ? "#fff" : (i < 5 ? BRAND : "#EF4444"),
          }}>{g[0]}</div>
        ))}
      </div>
      <Badge>Impostazioni → Orari</Badge>
    </Frame>
  );
}

function TerminiCondizioni() {
  const [n, setN] = useState(0);
  useEffect(() => { const t = setInterval(() => setN(v => (v+1)%2), 1400); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <BigIcon><IconClipboard size={28} /></BigIcon>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--ink)" }}>
        {n === 0 ? "Scrivi le condizioni" : "Il cliente le accetta"}
      </div>
      <Badge color={n === 1 ? "#15803D" : BRAND} bg={n === 1 ? "#DCFCE7" : "rgba(27,45,107,.08)"}>
        {n === 1 ? "✓ Accettazione obbligatoria" : "Impostazioni → Termini"}
      </Badge>
    </Frame>
  );
}

// ── Tutorial 2 — Veicoli ──────────────────────────────────────────────────────

function TipoVeicolo() {
  const [t, setT] = useState('auto');
  useEffect(() => { const iv = setInterval(() => setT(v => v === 'auto' ? 'moto' : 'auto'), 1800); return () => clearInterval(iv); }, []);
  return (
    <Frame>
      <BigIcon spin>{t === 'auto' ? <IconCar size={28} /> : <IconMoto size={28} />}</BigIcon>
      <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--ink)" }}>
        {t === 'auto' ? 'Fiat 500' : 'Vespa 125'}
      </div>
      <Badge>Catalogo → Nuovo veicolo</Badge>
    </Frame>
  );
}

function FotoVeicolo() {
  const [n, setN] = useState(1);
  useEffect(() => { const t = setInterval(() => setN(v => (v%4)+1), 700); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <div style={{ display: "flex", gap: 8 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{
            width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, color: BRAND, transition: "all .3s",
            background: i <= n ? "rgba(27,45,107,.08)" : "#F8FAFC",
            border: `1.5px ${i <= n ? "solid" : "dashed"} ${i <= n ? BRAND : "var(--border2)"}`,
          }}>{i <= n ? <IconCar size={18} /> : "+"}</div>
        ))}
      </div>
      <div style={{ fontSize: ".92rem", color: "var(--muted)", maxWidth: 260 }}>La prima foto diventa la copertina nel catalogo</div>
      <Badge>Catalogo → Foto</Badge>
    </Frame>
  );
}

function PrezziGiorno() {
  const [d, setD] = useState(0);
  const giorni = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
  const prezzi = [35,35,35,35,40,55,50];
  useEffect(() => { const t = setInterval(() => setD(v => (v+1)%7), 500); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <div style={{ fontSize: "2.2rem", fontWeight: 900, color: BRAND, transition: "all .3s" }}>€{prezzi[d]}</div>
      <div style={{ fontSize: ".85rem", color: "var(--muted)", fontWeight: 600 }}>{giorni[d]}</div>
      <Badge>{d >= 4 ? "Tariffa weekend più alta" : "Catalogo → Prezzi"}</Badge>
    </Frame>
  );
}

function Pubblicazione() {
  const [on, setOn] = useState(true);
  useEffect(() => { const t = setInterval(() => setOn(v => !v), 1500); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <div style={{ width: 56, height: 32, borderRadius: 20, background: on ? "#22C55E" : "var(--border2)", position: "relative", transition: "background .3s" }}>
        <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: on ? 27 : 3, transition: "left .3s", boxShadow: "0 1px 4px rgba(0,0,0,.2)" }} />
      </div>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--ink)" }}>
        {on ? "Visibile nel catalogo" : "Nascosto ai clienti"}
      </div>
      <Badge color={on ? "#15803D" : "var(--muted)"} bg={on ? "#DCFCE7" : "var(--bg)"}>
        {on ? "● Prenotabile online" : "○ In pausa"}
      </Badge>
    </Frame>
  );
}

// ── Tutorial 3 — Prenotazioni ─────────────────────────────────────────────────

function NuovaPrenotazione() {
  const [i, setI] = useState(0);
  const rows = [["Cliente","Mario Bianchi"],["Veicolo","Fiat 500"],["Totale","€105,00"]];
  useEffect(() => { const t = setInterval(() => setI(v => (v+1)%3), 1400); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <BigIcon><IconCalendar size={28} /></BigIcon>
      <div>
        <div style={{ fontSize: ".78rem", color: "var(--muted)", fontWeight: 600, marginBottom: 4 }}>{rows[i][0]}</div>
        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--ink)" }}>{rows[i][1]}</div>
      </div>
      <Badge>Prenotazioni → Nuova</Badge>
    </Frame>
  );
}

function ConfermaRitiro() {
  const [done, setDone] = useState(false);
  useEffect(() => { const t = setInterval(() => setDone(v => !v), 1600); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <BigIcon spin={done}>{done ? <IconKey size={28} /> : <IconClock size={28} />}</BigIcon>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--ink)" }}>
        {done ? "Ritiro avvenuto" : "In attesa di ritiro"}
      </div>
      <Badge color={done ? "#15803D" : "#A16207"} bg={done ? "#DCFCE7" : "#FEF9C3"}>
        {done ? "✓ Confermato" : "Un click per confermare"}
      </Badge>
    </Frame>
  );
}

function ConfermaRiconsegna() {
  const [done, setDone] = useState(false);
  useEffect(() => { const t = setInterval(() => setDone(v => !v), 1600); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <BigIcon spin={done}>{done ? <IconCheck size={28} /> : <IconHourglass size={28} />}</BigIcon>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--ink)" }}>
        {done ? "Pratica chiusa" : "In riconsegna oggi"}
      </div>
      <Badge color={done ? "#7E22CE" : "#C2410C"} bg={done ? "#F3E8FF" : "#FFEDD5"}>
        {done ? "✓ Riconsegna avvenuta" : "In attesa di restituzione"}
      </Badge>
    </Frame>
  );
}

function StampaContratto() {
  const [gen, setGen] = useState(false);
  useEffect(() => { const t = setInterval(() => setGen(v => !v), 1500); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <BigIcon spin={gen}><IconDocument size={28} /></BigIcon>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--ink)" }}>
        {gen ? "Contratto pronto" : "Genera il PDF"}
      </div>
      <Badge color={gen ? "#15803D" : BRAND} bg={gen ? "#DCFCE7" : "rgba(27,45,107,.08)"}>
        {gen ? "✓ Scaricato" : "Un click dal pannello"}
      </Badge>
    </Frame>
  );
}

// ── Tutorial 4 — Pagamenti ────────────────────────────────────────────────────

function CollegaStripe() {
  const [c, setC] = useState(false);
  useEffect(() => { const t = setInterval(() => setC(v => !v), 1700); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <BigIcon spin={c}>{c ? <IconLink size={28} /> : <IconCreditCard size={28} />}</BigIcon>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--ink)" }}>
        {c ? "Stripe collegato" : "Collega il tuo conto"}
      </div>
      <Badge color={c ? "#15803D" : BRAND} bg={c ? "#DCFCE7" : "rgba(27,45,107,.08)"}>
        {c ? "✓ Pronto a incassare online" : "Impostazioni → Pagamenti"}
      </Badge>
    </Frame>
  );
}

function PagamentoCliente() {
  const [p, setP] = useState(false);
  useEffect(() => { const t = setInterval(() => setP(v => !v), 1600); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <div style={{ fontSize: "2.2rem", fontWeight: 900, color: BRAND }}>€105,00</div>
      <div style={{ fontSize: ".92rem", color: "var(--muted)" }}>Il cliente paga con carta sul tuo sito</div>
      <Badge color={p ? "#15803D" : BRAND} bg={p ? "#DCFCE7" : "rgba(27,45,107,.08)"}>
        {p ? "✓ Pagamento riuscito" : "Gestito in sicurezza da Stripe"}
      </Badge>
    </Frame>
  );
}

function ContrattoAutomatico() {
  return (
    <Frame>
      <BigIcon><IconDocument size={28} /></BigIcon>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--ink)" }}>Generato automaticamente</div>
      <Badge>Dati già compilati ad ogni pagamento</Badge>
    </Frame>
  );
}

function MonitoraIncassi() {
  const [b, setB] = useState(0);
  const bars = [40,65,50,85,70];
  useEffect(() => { const t = setInterval(() => setB(v => (v+1)%5), 500); return () => clearInterval(t); }, []);
  return (
    <Frame>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 60 }}>
        {bars.map((h, i) => (
          <div key={i} style={{ width: 14, borderRadius: "4px 4px 0 0", height: `${h}%`, background: i === b ? BRAND : "var(--border2)", transition: "background .3s" }} />
        ))}
      </div>
      <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--ink)" }}>€1.240,00</div>
      <Badge color="#15803D" bg="#DCFCE7">+18% rispetto al mese scorso</Badge>
    </Frame>
  );
}

// ── Icone (card + step visual) ───────────────────────────────────────────────
// Stile "duotone": contorno in stroke leggero + una o più forme piene che
// danno peso e profondità, invece di un semplice outline uniforme o di
// emoji. Tutte accettano `size` per essere riusate sia nelle card (24px)
// sia nei cerchi grandi degli step del tutorial (~28px).
const IconSettings = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <line x1="6" y1="4" x2="6" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".5"/>
    <line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".5"/>
    <line x1="18" y1="4" x2="18" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".5"/>
    <circle cx="6" cy="8.5" r="2.75" fill="currentColor"/>
    <circle cx="12" cy="15.5" r="2.75" fill="currentColor"/>
    <circle cx="18" cy="6.5" r="2.75" fill="currentColor"/>
  </svg>
);
const IconCar = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v9a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity=".55"/>
    <circle cx="7.5" cy="17.5" r="2.5" fill="currentColor"/>
    <circle cx="17.5" cy="17.5" r="2.5" fill="currentColor"/>
  </svg>
);
const IconMoto = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M15 6l-2 4H8.5L7 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity=".55"/>
    <path d="M15 6h3l1.5 4H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity=".55"/>
    <circle cx="5.5" cy="17.5" r="3.2" fill="currentColor"/>
    <circle cx="18.5" cy="17.5" r="3.2" fill="currentColor"/>
  </svg>
);
const IconCalendar = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="2.5" stroke="currentColor" strokeWidth="2" opacity=".55"/>
    <path d="M3 10h18" stroke="currentColor" strokeWidth="2" opacity=".55"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <rect x="6.25" y="13" width="4.5" height="4.5" rx="1.2" fill="currentColor"/>
  </svg>
);
const IconCreditCard = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="1" y="4" width="22" height="16" rx="2.5" stroke="currentColor" strokeWidth="2" opacity=".55"/>
    <rect x="1" y="8" width="22" height="3.4" fill="currentColor"/>
    <rect x="4" y="15" width="6" height="2.2" rx="1.1" fill="currentColor" opacity=".75"/>
  </svg>
);
const IconBuilding = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="4" y="3" width="12" height="18" rx="1" stroke="currentColor" strokeWidth="2" opacity=".55"/>
    <rect x="16" y="9" width="4" height="12" rx="1" stroke="currentColor" strokeWidth="2" opacity=".55"/>
    <rect x="6.8" y="6.3" width="2.3" height="2.3" rx=".5" fill="currentColor"/>
    <rect x="11.1" y="6.3" width="2.3" height="2.3" rx=".5" fill="currentColor"/>
    <rect x="6.8" y="10.9" width="2.3" height="2.3" rx=".5" fill="currentColor"/>
    <rect x="11.1" y="10.9" width="2.3" height="2.3" rx=".5" fill="currentColor"/>
    <rect x="8.8" y="15.3" width="4.4" height="5.7" rx=".6" fill="currentColor" opacity=".85"/>
  </svg>
);
const IconPalette = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 3a9 9 0 1 0 0 18c1.1 0 2-.9 2-2 0-.5-.2-.9-.5-1.3-.3-.4-.5-.8-.5-1.2 0-1.1.9-2 2-2h2.3c1.8 0 3.2-1.4 3.2-3.2C20.5 6.6 16.7 3 12 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" opacity=".55"/>
    <circle cx="7.5" cy="11" r="1.7" fill="currentColor"/>
    <circle cx="9.7" cy="6.8" r="1.7" fill="currentColor"/>
    <circle cx="14.5" cy="6.8" r="1.7" fill="currentColor"/>
    <circle cx="16.7" cy="11" r="1.7" fill="currentColor"/>
  </svg>
);
const IconClock = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity=".55"/>
    <path d="M12 7v5l3.3 2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconClipboard = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="5" y="4" width="14" height="17" rx="2" stroke="currentColor" strokeWidth="2" opacity=".55"/>
    <rect x="9" y="2.3" width="6" height="3" rx="1" fill="currentColor"/>
    <path d="M8 11.2h8M8 14.7h8M8 18.2h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity=".85"/>
  </svg>
);
const IconKey = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="7.5" cy="15.5" r="4.3" stroke="currentColor" strokeWidth="2" opacity=".55"/>
    <circle cx="7.5" cy="15.5" r="1.5" fill="currentColor"/>
    <path d="M11 12l7.5-7.5M15.3 4.5l2.2 2.2M12.6 7.2l2.2 2.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconCheck = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" fill="currentColor" opacity=".18"/>
    <path d="M7.5 12.5l3 3 6-6.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconHourglass = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M6 3h12M6 21h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".55"/>
    <path d="M7 3v1.2c0 2.6 2 4.4 4.3 5.3M17 3v1.2c0 2.6-2 4.4-4.3 5.3M7 21v-1.2c0-2.6 2-4.4 4.3-5.3M17 21v-1.2c0-2.6-2-4.4-4.3-5.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".55"/>
    <polygon points="9,5.5 15,5.5 12,9" fill="currentColor"/>
    <polygon points="9,18.5 15,18.5 12,15" fill="currentColor"/>
  </svg>
);
const IconDocument = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M7 2.5h7l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 20V4A1.5 1.5 0 0 1 7 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" opacity=".55"/>
    <path d="M14 2.5V6a1 1 0 0 0 1 1h3.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" opacity=".55"/>
    <rect x="8.5" y="11" width="7" height="1.8" rx=".6" fill="currentColor"/>
    <rect x="8.5" y="14.4" width="7" height="1.8" rx=".6" fill="currentColor" opacity=".75"/>
    <rect x="8.5" y="17.8" width="4.5" height="1.8" rx=".6" fill="currentColor" opacity=".55"/>
  </svg>
);
const IconLink = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M9 15l6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <path d="M10.3 6.3l1-1a4 4 0 0 1 5.7 5.7l-1.2 1.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".55"/>
    <path d="M13.7 17.7l-1 1a4 4 0 0 1-5.7-5.7l1.2-1.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity=".55"/>
  </svg>
);

// ── Dati tutorial ─────────────────────────────────────────────────────────────
const TUTORIALS = [
  {
    id: "configurazione",
    title: "Configurazione iniziale",
    desc: "Azienda, logo, orari e condizioni. Il tuo profilo pronto in 10 minuti.",
    icon: <IconSettings />,
    duration: "10 min",
    steps: [
      { title: "Informazioni azienda", desc: "Inserisci nome, indirizzo, telefono ed email. Questi dati appaiono sul sito pubblico e nei contratti PDF.", visual: <InfoAzienda /> },
      { title: "Logo e colori", desc: "Carica il logo e scegli il colore del tuo brand. Il sito dei tuoi clienti si aggiorna automaticamente.", visual: <LogoColori /> },
      { title: "Orari di apertura", desc: "Imposta i giorni e gli orari in cui accetti prenotazioni. Puoi variarli per ogni giorno della settimana.", visual: <OrariApertura /> },
      { title: "Termini e condizioni", desc: "Scrivi le condizioni di noleggio. Il cliente le vede e le accetta prima di prenotare.", visual: <TerminiCondizioni /> },
    ],
  },
  {
    id: "veicoli",
    title: "Aggiungere veicoli",
    desc: "Auto e moto con foto, prezzi differenziati e pubblicazione online.",
    icon: <IconCar />,
    duration: "6 min",
    steps: [
      { title: "Tipo veicolo", desc: "Scegli auto o moto e inserisci marca, modello e targa. Il modulo si adatta al tipo scelto.", visual: <TipoVeicolo /> },
      { title: "Foto del veicolo", desc: "Carica più foto: la prima diventa la copertina mostrata nel catalogo pubblico.", visual: <FotoVeicolo /> },
      { title: "Prezzi per giorno", desc: "Imposta un prezzo diverso per ogni giorno. Il weekend può costare di più.", visual: <PrezziGiorno /> },
      { title: "Pubblicazione", desc: "Attiva la visibilità per renderlo prenotabile online. Disattivala quando serve, senza cancellarlo.", visual: <Pubblicazione /> },
    ],
  },
  {
    id: "prenotazioni",
    title: "Gestire le prenotazioni",
    desc: "Dal ritiro alla riconsegna, con contratto generato in automatico.",
    icon: <IconCalendar />,
    duration: "7 min",
    steps: [
      { title: "Nuova prenotazione", desc: "Scegli cliente, veicolo e date. Il totale si calcola da solo in base ai prezzi impostati.", visual: <NuovaPrenotazione /> },
      { title: "Conferma il ritiro", desc: "Quando il cliente ritira il veicolo, un click aggiorna lo stato e lo segna come in uso.", visual: <ConfermaRitiro /> },
      { title: "Conferma la riconsegna", desc: "Alla restituzione, chiudi la pratica: le date si liberano per nuove prenotazioni.", visual: <ConfermaRiconsegna /> },
      { title: "Stampa il contratto", desc: "Genera il PDF in due pagine, con tutti i dati già compilati.", visual: <StampaContratto /> },
    ],
  },
  {
    id: "pagamenti",
    title: "Pagamenti online",
    desc: "Collega Stripe, incassa con carta e monitora i guadagni ogni mese.",
    icon: <IconCreditCard />,
    duration: "5 min",
    steps: [
      { title: "Collega Stripe", desc: "Verifica identità e conto bancario su Stripe, poi torni automaticamente qui.", visual: <CollegaStripe /> },
      { title: "Il cliente paga online", desc: "Pagamento con carta gestito in sicurezza da Stripe, direttamente sul tuo sito.", visual: <PagamentoCliente /> },
      { title: "Contratto automatico", desc: "Ad ogni prenotazione pagata, il contratto si genera da solo con tutti i dati.", visual: <ContrattoAutomatico /> },
      { title: "Monitora gli incassi", desc: "Statistiche mese per mese per seguire l'andamento del tuo noleggio.", visual: <MonitoraIncassi /> },
    ],
  },
];

// ── Card tutorial — semplice, un tap e via ────────────────────────────────────
function TutorialCard({ t, onStart, isActive }) {
  return (
    <button
      onClick={onStart}
      style={{
        position: "relative",
        display: "flex", alignItems: "center", gap: "1.1rem", textAlign: "left",
        background: "#fff", border: `1.5px solid ${isActive ? BRAND : "var(--border)"}`,
        borderRadius: 18, padding: "1.35rem 1.5rem", width: "100%", cursor: "pointer",
        boxShadow: isActive ? "0 0 0 4px rgba(27,45,107,.08)" : "none",
        transition: "all .2s", fontFamily: "inherit",
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = "var(--border2)"; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = "var(--border)"; }}
    >
      <div style={{
        width: 54, height: 54, borderRadius: 16, flexShrink: 0,
        background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 100%)`,
        boxShadow: `0 6px 16px rgba(27,45,107,.28), inset 0 1px 0 rgba(255,255,255,.18)`,
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -14, left: -10, width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,.14)" }} />
        <div style={{ position: "relative" }}>{t.icon}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "1.02rem", fontWeight: 800, color: "var(--ink)", marginBottom: 3 }}>{t.title}</div>
        <div style={{ fontSize: ".85rem", color: "var(--muted)", lineHeight: 1.5 }}>{t.desc}</div>
      </div>
      <div style={{ fontSize: ".76rem", fontWeight: 700, color: "var(--muted)", flexShrink: 0, whiteSpace: "nowrap", marginRight: 18 }}>{t.duration}</div>
    </button>
  );
}

// ── Viewer — un solo step visibile alla volta, niente rumore ────────────────
function TutorialViewer({ t, onClose }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 20); return () => clearTimeout(t); }, []);
  const total = t.steps.length;
  const s = t.steps[step];
  const isLast = step === total - 1;

  return (
    <div style={{
      background: "#fff", borderRadius: 22, border: "1px solid var(--border)", overflow: "hidden", boxShadow: "var(--sh-lg)",
      opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)",
      transition: "opacity .45s ease, transform .45s ease",
    }}>

      {/* Header minimale */}
      <div style={{ padding: "1.5rem 2rem 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: ".85rem", fontWeight: 700, color: "var(--muted)" }}>{t.title}</div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: "1.1rem", padding: 4, lineHeight: 1 }} aria-label="Chiudi">✕</button>
      </div>

      {/* Progress: puntini semplici */}
      <div style={{ display: "flex", gap: 7, padding: "1rem 2rem 0" }}>
        {t.steps.map((_, i) => (
          <div
            key={i}
            onClick={() => setStep(i)}
            style={{
              flex: 1, height: 4, borderRadius: 10, cursor: "pointer", transition: "background .3s",
              background: i <= step ? BRAND : "var(--border)",
            }}
          />
        ))}
      </div>

      {/* Contenuto centrato, una cosa alla volta */}
      <div style={{ padding: "2rem 2rem 2.5rem", maxWidth: 480, margin: "0 auto" }}>
        <div style={{ fontSize: ".8rem", fontWeight: 700, color: BRAND, marginBottom: 6 }}>Passo {step + 1} di {total}</div>
        <h3 style={{ fontSize: "1.4rem", fontWeight: 800, color: "var(--ink)", margin: "0 0 .6rem" }}>{s.title}</h3>
        <p style={{ fontSize: ".97rem", color: "var(--muted)", lineHeight: 1.7, margin: "0 0 1.75rem" }}>{s.desc}</p>

        <div style={{ marginBottom: "2rem" }}>
          <StepVisual step={s} />
        </div>

        {/* Navigazione */}
        <div style={{ display: "flex", gap: 10 }}>
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              style={{ background: "#fff", color: "var(--muted)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "12px 20px", fontSize: ".92rem", fontWeight: 700, cursor: "pointer", flex: "0 0 auto" }}
            >
              ← Indietro
            </button>
          )}
          {!isLast ? (
            <button
              onClick={() => setStep(step + 1)}
              style={{ background: BRAND, color: "#fff", border: "none", borderRadius: 12, padding: "12px 22px", fontSize: ".92rem", fontWeight: 700, cursor: "pointer", flex: 1, transition: "opacity .2s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = ".88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              Avanti →
            </button>
          ) : (
            <button
              onClick={onClose}
              style={{ background: "#22C55E", color: "#fff", border: "none", borderRadius: 12, padding: "12px 22px", fontSize: ".92rem", fontWeight: 700, cursor: "pointer", flex: 1, transition: "opacity .2s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = ".88"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              ✓ Fatto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────────
export default function Tutorial({ go }) {
  useAnim();
  useSEO({ ...PAGE_SEO.tutorial, schema: BREADCRUMBS.tutorial });
  const [activeTutorial, setActiveTutorial] = useState(null);
  const viewerRef = useRef(null);

  const handleStart = (t) => {
    setActiveTutorial(t);
    setTimeout(() => viewerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  return (
    <>
      <Breadcrumb items={BREADCRUMB_ITEMS.tutorial} />
      <section className="ph">
        <div className="ph-in an">
          <img
            src={logoHeader}
            alt="NoleggioinCloud"
            style={{ height: 40, width: "auto", objectFit: "contain", marginBottom: "1.1rem" }}
          />
          <div className="chip">Tutorial</div>
          <h1 className="h2" style={{ fontSize: "clamp(1.9rem,4vw,2.9rem)" }}>
            Impara a usare<br /><mark>NoleggioinCloud</mark>
          </h1>
          <p className="lead" style={{ marginTop: ".65rem" }}>
            Quattro guide brevi, un passo alla volta.
          </p>
        </div>
      </section>

      <section className="sec" style={{ background: "var(--bg)" }}>
        <div className="wrap" style={{ maxWidth: 560 }}>

          {/* Lista tutorial — colonna singola, semplice */}
          <div className="an" style={{ display: "flex", flexDirection: "column", gap: ".9rem", marginBottom: activeTutorial ? "2.5rem" : 0 }}>
            {TUTORIALS.map(t => (
              <TutorialCard
                key={t.id}
                t={t}
                isActive={activeTutorial?.id === t.id}
                onStart={() => handleStart(t)}
              />
            ))}
          </div>

          {/* Viewer */}
          {activeTutorial && (
            <div ref={viewerRef}>
              <TutorialViewer
                t={activeTutorial}
                onClose={() => setActiveTutorial(null)}
              />
            </div>
          )}

          {!activeTutorial && (
            <p style={{ textAlign: "center", fontSize: ".88rem", color: "var(--muted)", marginTop: "2rem" }}>
              Non trovi quello che cerchi? <Link to="/contatti" style={{ color: BRAND, fontWeight: 700, textDecoration: "none" }}>Scrivici</Link>
            </p>
          )}

        </div>
      </section>

      <CtaBand go={go} />
    </>
  );
}
